import type { Period } from "../../funnel-detail/types";

type TimeBucketInput<T> = {
  period: Period;
  rows: T[];
  getDate: (row: T, index: number) => Date | string | null | undefined;
  includeEmpty?: boolean;
};

export type TimeBucket<T> = {
  x: number;
  label: string;
  tooltipLabel: string;
  startDate: Date;
  endDate: Date;
  bucketDays: number;
  rows: T[];
};

const SHORT_MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const startOfDay = (value: Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
};

export const toDateKey = (value: Date) => {
  const date = startOfDay(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseChartDate = (value: Date | string | null | undefined) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return startOfDay(date);
};

export const resolvePeriodDayDate = (period: Period, day: number, fallbackIndex = 0) => {
  const offset = Math.max(0, (Number.isFinite(day) ? day : fallbackIndex + 1) - 1);
  return addDays(period.from, offset);
};

export const getBucketDaysForPeriod = (period: Period) => {
  const days = Math.max(1, period.days || 1);
  const rawBucketDays = Math.ceil(days / 30);
  const steps = [1, 2, 3, 5, 7, 10, 14, 15, 30, 60, 90];
  return steps.find((step) => step >= rawBucketDays) ?? 90;
};

const formatBucketLabel = (startDate: Date, endDate: Date) => {
  const formatDate = (date: Date) => `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`;

  if (toDateKey(startDate) === toDateKey(endDate)) {
    return formatDate(startDate);
  }

  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  if (sameMonth) {
    return `${startDate.getDate()}-${endDate.getDate()} ${SHORT_MONTHS[endDate.getMonth()]}`;
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export function buildTimeBuckets<T>({ period, rows, getDate, includeEmpty = false }: TimeBucketInput<T>) {
  const bucketDays = getBucketDaysForPeriod(period);
  const from = startOfDay(period.from);
  const to = startOfDay(period.to);
  const rowsByDate = new Map<string, T[]>();

  rows.forEach((row, index) => {
    const date = parseChartDate(getDate(row, index));
    if (!date) return;
    const key = toDateKey(date);
    const existing = rowsByDate.get(key) ?? [];
    existing.push(row);
    rowsByDate.set(key, existing);
  });

  const buckets: TimeBucket<T>[] = [];
  let bucketIndex = 1;

  for (let startDate = from; startDate <= to; startDate = addDays(startDate, bucketDays)) {
    const endDate = new Date(Math.min(addDays(startDate, bucketDays - 1).getTime(), to.getTime()));
    const bucketRows: T[] = [];

    for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
      bucketRows.push(...(rowsByDate.get(toDateKey(date)) ?? []));
    }

    if (includeEmpty || bucketRows.length > 0) {
      buckets.push({
        x: bucketIndex,
        label: formatBucketLabel(startDate, endDate),
        tooltipLabel: formatBucketLabel(startDate, endDate),
        startDate,
        endDate,
        bucketDays,
        rows: bucketRows,
      });
    }

    bucketIndex += 1;
  }

  return buckets;
}

export const describeBucketSize = (bucketDays: number) =>
  bucketDays <= 1 ? "Día a día" : `Cada ${bucketDays} días`;

export function buildSmartYAxisDomain(values: number[]) {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length === 0) return [0, 1] as [number, number];

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  if (min === max) {
    const padding = Math.max(Math.abs(max) * 0.18, 1);
    return [Math.max(0, min - padding), max + padding] as [number, number];
  }

  const span = max - min;
  const magnitude = Math.max(Math.abs(min), Math.abs(max), 1);
  const needsExtraBreathingRoom = span / magnitude < 0.24;
  const padding = Math.max(span * 0.28, magnitude * (needsExtraBreathingRoom ? 0.14 : 0.08));
  const lower = min >= 0 ? Math.max(0, min - padding) : min - padding;
  const upper = max + padding;

  return [lower, upper] as [number, number];
}
